import React, { memo } from 'react';
import { Box, Chip, InputBase, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { SubjectStatus } from '../../../types';
import { CHOOSABLE_STATUSES, PAPER_STATUS, PaperDraft, chooseStatus, typeMark } from './paperStatus';

interface Props {
  draft: PaperDraft;
  maxMarks: number;
  /** Submitted papers are read-only until unlocked. */
  locked?: boolean;
  /** Who the paper belongs to, for screen readers ("Marks for Ali"). */
  label: string;
  error?: boolean;
  onChange: (next: PaperDraft) => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

/**
 * One paper being entered: the marks box and the status chips. Typing a
 * mark makes the student present; a chip sets absent or not appeared, and
 * clicking the chosen chip again clears the paper. Used on a marks sheet
 * (a row per student) and on a student's results (a row per subject).
 */
const PaperInput: React.FC<Props> = ({ draft, maxMarks, locked, label, error, onChange, inputRef, onKeyDown }) => {
  const theme = useTheme();
  const boxed = draft.status === SubjectStatus.ABSENT || draft.status === SubjectStatus.NOT_APPEARED;
  const over = draft.marks !== '' && Number(draft.marks) > maxMarks;
  const bad = over || !!error;

  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
      <Box
        sx={{
          display: 'inline-flex', alignItems: 'center', width: 112, height: 36, px: 1.25, borderRadius: '8px',
          border: 1, borderColor: bad ? 'error.main' : 'divider',
          bgcolor: locked || boxed ? alpha(theme.palette.text.primary, 0.03) : 'background.paper',
          transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: 120 }),
          '&:focus-within': { borderColor: bad ? 'error.main' : 'primary.main', boxShadow: `0 0 0 3px ${alpha(bad ? theme.palette.error.main : theme.palette.primary.main, 0.15)}` },
        }}
      >
        <InputBase
          inputRef={inputRef}
          value={draft.marks}
          onChange={e => { const next = typeMark(e.target.value); if (next) onChange(next); }}
          onKeyDown={onKeyDown}
          onFocus={e => e.target.select()}
          disabled={locked || boxed}
          placeholder={boxed ? PAPER_STATUS[draft.status].short : ''}
          inputProps={{ inputMode: 'decimal', 'aria-label': `Marks for ${label}`, style: { fontWeight: 700, fontVariantNumeric: 'tabular-nums', padding: 0 } }}
          sx={{ flex: 1 }}
        />
        <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>/ {maxMarks}</Typography>
      </Box>
      <Stack direction="row" spacing={0.5}>
        {CHOOSABLE_STATUSES.map(status => {
          const meta = PAPER_STATUS[status];
          const on = draft.status === status;
          const color = meta.tone === 'neutral' ? theme.palette.text.primary : theme.palette[meta.tone as 'success' | 'error'].main;
          return (
            <Tooltip key={status} title={meta.hint} disableInteractive enterDelay={500}>
              <Chip
                label={meta.label}
                size="small"
                tabIndex={-1}
                clickable={!locked}
                onClick={locked ? undefined : () => onChange(chooseStatus(draft, status))}
                sx={{
                  height: 24, fontWeight: 600, fontSize: '0.72rem',
                  bgcolor: on ? alpha(color, meta.tone === 'neutral' ? 0.1 : 0.14) : 'transparent',
                  color: on ? (meta.tone === 'neutral' ? 'text.primary' : `${meta.tone}.main`) : 'text.secondary',
                  border: `1px solid ${alpha(color, on ? 0.4 : 0.16)}`,
                  opacity: locked && !on ? 0.5 : 1,
                }}
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default memo(PaperInput);
